import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useContext } from 'react';
import { Button as AntdButton, ConfigProvider } from 'antd';
import { Icon } from '../icons/icon.js';
import { blockedButtonProps, useBlockedPress, useBlockedReason } from './blocked-reason.js';
import { Tooltip } from './overlays.js';
import { usePopupTriggerAria } from './tooltip-context.js';
export { blockedButtonProps } from './blocked-reason.js';
/**
 * The single tree every kit button renders: one kit `Tooltip` (idle with an
 * empty title on the normal path) around one AntD `Button`. A reason
 * appearing or clearing only changes props, so the `<button>` is never
 * remounted and keyboard focus stays on it.
 */
const ButtonCore = forwardRef(function ButtonCore({ tip, reason, description, ...props }, ref) {
    const prefixCls = useContext(ConfigProvider.ConfigContext).getPrefixCls('btn');
    const popupAria = usePopupTriggerAria(props);
    const onBlockedPress = useBlockedPress();
    return (_jsx(Tooltip, { kind: tip.kind, title: tip.title, placement: tip.placement, tapToPin: tip.tapToPin, children: _jsx(AntdButton, { ref: ref, ...props, ...popupAria, ...(!reason && description ? { 'aria-description': description } : null), ...(reason ? blockedButtonProps(props, reason, prefixCls, onBlockedPress) : null) }) }));
});
/** The word before a text button's shortcut when the bubble has nothing else to say. */
const SHORTCUT_LABEL = 'میان‌بر';
/** How a text button's bubble is configured: the reason, else the hint (+ key), else the key. */
function textButtonTip(reason, hint, shortcut) {
    if (reason)
        return { kind: 'help', title: reason, tapToPin: true };
    if (hint) {
        return {
            kind: 'description',
            title: shortcut ? _jsx(TipText, { text: hint, shortcut: shortcut }) : hint,
            tapToPin: false,
        };
    }
    if (shortcut) {
        // The key is already `aria-keyshortcuts`; the bubble only repeats it for the eye.
        return { kind: 'label', title: _jsx(TipText, { text: SHORTCUT_LABEL, shortcut: shortcut }), tapToPin: false };
    }
    return { kind: 'description', title: '', tapToPin: false };
}
const KitButton = forwardRef(function Button({ disabledReason, hint, shortcut, ...props }, ref) {
    const reason = useBlockedReason(disabledReason);
    return (_jsx(ButtonCore, { ref: ref, ...props, "aria-keyshortcuts": shortcut ?? props['aria-keyshortcuts'], reason: reason, description: hint, tip: textButtonTip(reason, hint, shortcut) }));
});
/**
 * AntD's `Button` behind the kit's one import surface, plus a blocked state
 * that explains itself (`disabledReason`) and an optional description (`hint`).
 *
 * It is ONE element tree whether or not it is blocked, so a reason appearing or
 * clearing never remounts the `<button>` and keyboard focus stays on it.
 * `__ANT_BUTTON` is carried over because antd sniffs it (`Dropdown.Button`,
 * `Input.Search enterButton`, `Space.Compact`) to treat the child as a button.
 */
export const Button = Object.assign(KitButton, { __ANT_BUTTON: true });
/** The label part of an icon button's bubble, with its shortcut. */
function TipText({ text, shortcut }) {
    if (!shortcut)
        return _jsx(_Fragment, { children: text });
    return (_jsxs(_Fragment, { children: [text, ' ', _jsx("bdi", { dir: "ltr", className: "bk-tip__shortcut", children: _jsx("kbd", { children: shortcut }) })] }));
}
/**
 * A button whose whole content is one glyph.
 *
 * Split out from `Button` because the icon-only case has requirements the
 * general one does not: an accessible name (enforced by the type), a glyph
 * from the registry rather than an arbitrary node, and a LABEL TOOLTIP — the
 * bubble a sighted mouse user reads instead of the name. The bubble repeats
 * the name, so it is hidden from assistive tech (kind `label`); a `hint` is a
 * real description and is announced; a blocked reason is announced and shown
 * under the name.
 */
export const IconButton = forwardRef(function IconButton({ name, label, active = false, circle = true, size, className, tooltip, tooltipPlacement, hint, shortcut, disabledReason, ...rest }, ref) {
    const reason = useBlockedReason(disabledReason);
    const glyph = size === 'small' ? 16 : size === 'large' ? 24 : 20;
    const visible = tooltip === false ? null : tooltip || label;
    let tip;
    if (reason) {
        tip = {
            kind: 'help',
            tapToPin: true,
            placement: tooltipPlacement,
            title: (_jsxs(_Fragment, { children: [_jsx("span", { className: "bk-tip__title", "aria-hidden": "true", children: tooltip || label }), _jsx("span", { className: "bk-tip__body", children: reason })] })),
        };
    }
    else if (visible !== null && hint) {
        tip = {
            kind: 'description',
            tapToPin: false,
            placement: tooltipPlacement,
            title: (_jsxs(_Fragment, { children: [_jsx("span", { className: "bk-tip__title", "aria-hidden": "true", children: _jsx(TipText, { text: visible, shortcut: shortcut }) }), _jsx("span", { className: "bk-tip__body", children: hint })] })),
        };
    }
    else {
        tip = {
            kind: 'label',
            tapToPin: false,
            placement: tooltipPlacement,
            title: visible === null ? '' : _jsx(TipText, { text: visible, shortcut: shortcut }),
        };
    }
    return (_jsx(ButtonCore, { ...rest, ref: ref, reason: reason, description: hint, tip: tip, 
        // `.bk-icon-button` grows the HIT AREA to 44px on a coarse pointer
        // (styles/ui.css) without changing the drawn size, so a header or a
        // table row keeps its density while a thumb still lands on the glyph.
        className: className ? `bk-icon-button ${className}` : 'bk-icon-button', size: size, shape: circle ? 'circle' : 'default', "aria-label": label, "aria-keyshortcuts": shortcut, icon: _jsx(Icon, { name: name, size: glyph, active: active }) }));
});
//# sourceMappingURL=button.js.map